import { useCallback } from 'react';
import { FixedNumber } from '@ethersproject/bignumber';
import { TokenAsset } from '@types';
import { TokenQuotePair } from './types';

function useGetTokenQuoteConfig(): (
  tokenAsset: TokenAsset
) => TokenQuotePair | null {
  return useCallback((tokenAsset: TokenAsset): TokenQuotePair | null => {
    switch (tokenAsset.symbol) {
      case 'ETH':
      case 'WETH':
        return {
          baseTokenSymbol: 'ETH',
          quoteTokenSymbol: 'ETH',
          toPriceInEth: (tokenPrice: FixedNumber): FixedNumber => tokenPrice,
        };
      case 'WBTC':
        // Supported by - CDC, Binance
        return {
          baseTokenSymbol: 'ETH',
          quoteTokenSymbol: 'BTC',
          // Price the Token Asset in ETH by inversing.
          // Ex.: Convert ETH-BTC (price of 1 ETH in BTC) to BTC-ETH (price of 1 BTC in ETH)
          toPriceInEth: (tokenPrice: FixedNumber): FixedNumber =>
            FixedNumber.from(1).divUnsafe(tokenPrice),
        };
      case 'DAI':
        // Supported by - Binance, Kraken
        return {
          baseTokenSymbol: 'ETH',
          quoteTokenSymbol: 'DAI',
          toPriceInEth: (tokenPrice: FixedNumber): FixedNumber =>
            FixedNumber.from(1).divUnsafe(tokenPrice),
        };
      case 'USDT':
        // Supported by - CDC, Binance, Karken, FTX, Okex
        return {
          baseTokenSymbol: 'ETH',
          quoteTokenSymbol: 'USDT',
          toPriceInEth: (tokenPrice: FixedNumber): FixedNumber =>
            FixedNumber.from(1).divUnsafe(tokenPrice),
        };
      case 'USDC':
        // Supported by - CDC, Binance, Kraken
        return {
          baseTokenSymbol: 'ETH',
          quoteTokenSymbol: 'USDC',
          toPriceInEth: (tokenPrice: FixedNumber): FixedNumber =>
            FixedNumber.from(1).divUnsafe(tokenPrice),
        };
      case 'CRO':
        // Supported by - CDC, Okex, Huobi
        return {
          baseTokenSymbol: 'CRO',
          quoteTokenSymbol: 'USD',
          // Price the Token Asset in ETH by multiplying by ETH-USD
          // EX.: Convert CRO-USD (price of 1 CRO in USD) to CRO-ETH (price of 1 CRO in ETH) by:
          // 1. 1 / ETH-USD = USD-ETH
          // 2. CRO-USD * USD-ETH = CRO-ETH
          toPriceInEth: (
            tokenPrice: FixedNumber,
            ethUsdPrice: FixedNumber
          ): FixedNumber =>
            FixedNumber.from(1).divUnsafe(ethUsdPrice).mulUnsafe(tokenPrice),
        };
      default:
        return null;
    }
  }, []);
}

export default useGetTokenQuoteConfig;
